import React from 'react';
import {
  GridRowParams,
  getGridStringOperators,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid';
import { IconButton, Typography } from '@material-ui/core';
import Warning from '@material-ui/icons/Warning';
import CheckCircle from '@material-ui/icons/CheckCircle';
import EditIcon from '@material-ui/icons/Edit';
import { CustomChip } from '../components/CustomChip';
import { ChipSet } from '../components/ChipSet';
import { getUserOrganizationNames } from '../utils';
import type { User } from '@app/shared/types';

const stringOperators = getGridStringOperators().filter(op =>
  ['contains'].includes(op.value),
);
const singleSelectOperators = getGridSingleSelectOperators().filter(
  op => op.value === 'is',
);

export const UserColumns = [
  {
    flex: 1,
    minWidth: 90,
    field: 'email',
    headerName: 'User Email',
    resizable: true,
    disableColumnMenu: true,
    sortable: true,
    filterOperators: stringOperators,
    renderCell: (params: GridRowParams) => (
      <Typography
        style={{ fontSize: 14, fontFamily: '' }}
        variant="body1"
        component="span"
        color="primary"
      >
        {params.row.email}
      </Typography>
    ),
  },
  {
    flex: 1,
    field: 'username',
    minWidth: 90,
    headerName: 'User Name',
    resizable: true,
    disableColumnMenu: true,
    sortable: true,
    filterOperators: stringOperators,
  },
  {
    flex: 1,
    field: 'organizations',
    headerName: 'Teams',
    minWidth: 150,
    resizable: true,
    disableColumnMenu: true,
    sortable: false,
    filterOperators: stringOperators,
    valueGetter: (_value: unknown, row: User) => getUserOrganizationNames(row),
    renderCell: (params: GridRowParams) => (
      <ChipSet max={1} values={getUserOrganizationNames(params.row as User)} />
    ),
  },
  {
    flex: 1,
    minWidth: 135,
    field: 'isInternal',
    headerName: 'Access Category',
    resizable: true,
    disableColumnMenu: true,
    sortable: true,
    type: 'singleSelect',
    valueOptions: [
      { value: true, label: 'Internal' },
      { value: false, label: 'External' },
    ],
    filterOperators: singleSelectOperators,
    renderCell: (params: GridRowParams) => (
      <CustomChip
        label={params.row.isInternal ? 'Internal' : 'External'}
        variant="outlined"
        isInternal={params.row.isInternal}
      />
    ),
  },
  {
    flex: 1,
    minWidth: 435,
    field: 'attributes',
    headerName: 'Permissions',
    resizable: true,
    disableColumnMenu: true,
    sortable: false,
    filterOperators: stringOperators,
    renderCell: (params: GridRowParams) => (
      <ChipSet max={1} values={params.row.attributes} hideTooltip />
    ),
  },
  {
    flex: 1,
    field: 'isActive',
    minWidth: 120,
    headerName: 'Status',
    resizable: true,
    disableColumnMenu: true,
    sortable: true,
    type: 'singleSelect',
    valueOptions: [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' },
    ],
    filterOperators: singleSelectOperators,
    renderCell: (params: GridRowParams) => (
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          height: '100%',
        }}
      >
        {params.row.isActive ? (
          <>
            <CheckCircle htmlColor="green" /> Active
          </>
        ) : (
          <>
            <Warning color="error" />
            Deactivated
          </>
        )}
      </span>
    ),
  },
  {
    flex: 1,
    field: 'edit',
    headerName: 'Edit',
    resizable: true,
    disableColumnMenu: true,
    sortable: false,
    filterable: false,
    renderCell: (
      params: GridRowParams & { colDef: { onClick: (rowId: string) => {} } },
    ) => (
      <IconButton
        type="button"
        onClick={e => {
          e.stopPropagation();
          params.colDef.onClick(params.row.id);
        }}
      >
        <EditIcon color="primary" />
      </IconButton>
    ),
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone Number',
    filterable: true,
    hideable: false,
    filterOperators: stringOperators,
  },
  {
    field: 'companyName',
    headerName: 'Company Name',
    filterable: true,
    hideable: false,
    filterOperators: stringOperators,
  },
];
